import _ from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useObserver } from 'mobx-react-lite';

import useUI from 'hooks/use-ui';
import { CategoriesWithPotencies } from 'shared/constants';
import { useHasFilterableSpecials } from 'src/dispensary/hooks/use-has-filterable-specials';
import type { FilterVisibility, MenuFilterOptions, SidebarCategory, SidebarDispensary } from './types';

type UseFilterVisibilityInput = {
  category?: SidebarCategory | null;
  customCategory?: unknown;
  dispensary?: SidebarDispensary | null;
  menuFilterOptions: MenuFilterOptions;
  onCollectionsPage?: boolean;
  onSearchPage?: boolean;
  route?: string;
};

/**
 * Computes which filter blocks the host would render in the menu sidebar.
 * Single source of truth for both the stock sidebar and the SDK FilterSidePanel bridge.
 */
export function useFilterVisibility({
  category,
  customCategory,
  dispensary,
  menuFilterOptions,
  onCollectionsPage,
  onSearchPage,
  route,
}: UseFilterVisibilityInput): FilterVisibility {
  const flags = useFlags();
  const UI = useUI();
  const isOnSubcategoryPage = useObserver(() => UI.onSubcategoryPage);

  const { brands, subcategories, strainTypes, weights, effects, terpenes } = menuFilterOptions;
  const onBrandsPage = _.includes(route, 'brands');
  const hidePotencyFilters = dispensary?.storeSettings?.hideFilters ?? false;
  const showSubcategories = !_.isEmpty(subcategories) && !onBrandsPage;

  const isMultiSelectSubcategoryFilterEnabled = flags['ecomm.multi-filter-subcategories.rollout'] ?? false;
  const specialsRedesignEnabled = flags['ecomm.specials-redesign.rollout'] ?? false;
  // Gated rollout: enables the category/subcategory filter for custom categories (e.g. "ALL"),
  // targetable by ecomm-dispensary/ecomm-enterprise LD context.
  const customCategorySubcategoryFiltersEnabled = flags['ecomm.custom-category-subcategory-filters.rollout'] ?? false;
  const { hasFilterableData } = useHasFilterableSpecials({ skip: !specialsRedesignEnabled });

  return {
    isMultiSelectSubcategoryFilterEnabled,
    onBrandsPage,
    showBrands: !_.isEmpty(brands),
    // Custom categories (e.g. "ALL") are gated behind customCategorySubcategoryFiltersEnabled:
    // the filter options are already scoped to this collection's own products server-side (see
    // menu-filters-v2), so there's no data-correctness risk, but rollout is flag-controlled so
    // it can be targeted/killed per dispensary or enterprise.
    showCategorySubcategoryFilter: Boolean(
      (Boolean(onSearchPage) || Boolean(onCollectionsPage)) &&
        (!customCategory || customCategorySubcategoryFiltersEnabled)
    ),
    showEffects: !_.isEmpty(effects) && !onBrandsPage && !(dispensary?.storeSettings?.hideEffects ?? false),
    showPotencies: !hidePotencyFilters && _.includes(CategoriesWithPotencies, category?.key) && !onBrandsPage,
    showSpecialsFilter: specialsRedesignEnabled && hasFilterableData,
    showSubcategoriesFilterMultiSelect: isMultiSelectSubcategoryFilterEnabled && !isOnSubcategoryPage,
    showSubcategoryFilter: Boolean(showSubcategories && !onSearchPage && !onCollectionsPage && !customCategory),
    showStrainTypes: !_.isEmpty(strainTypes) && !onBrandsPage,
    showTerpenes: !_.isEmpty(terpenes),
    showWeights: (weights?.length ?? 0) > 1 && !onBrandsPage,
  };
}
